import { type FunctionComponent, useState } from 'react';
import { FEATURE_FLAGS, OVERLAY_MARKER } from '../../../constants/general';
import { HOMEPAGE_LINK, SHOWROOM_LINK, SUPPORT_FAQ_LINK, SUPPORT_HOME_LINK } from '../../../constants/links';
import { SearchBarProvider } from '../../../contexts/search-bar/search-bar.context';
import { useFeatures } from '../../../hooks/features/features.hooks';
import { Link } from '../../common-components/link/link.component';
import { ProjectHeaderDropdown } from '../../project-components/project-header-dropdown/project-header-dropdown.component';
import OmniHomeSvg from '../../svgs/omni-home.svg';
import { AccountDropdown } from '../account-dropdown/account-dropdown.component';
import { AnnouncementBanner } from '../announcement-banner/announcement-banner.component';
import { CallCenterLink } from '../call-center-link/call-center-link.component';
import { CartLink } from '../cart-link/cart-link.component';
import { ChatButton } from '../chat-button/chat-button.component';
import { DesktopNav } from '../desktop-nav/desktop-nav.component';
import { GlobalContentConstruct } from '../global-content-construct/global-content-construct.component';
import { MobileNav } from '../mobile-nav/mobile-nav.component';
import { NotificationsBadge } from '../notifications-badge/notifications-badge.component';
import { ProSignupLink } from '../pro-signup-link/pro-signup-link.component';
import { SearchBar } from '../search-bar/search-bar.component';
import { UniversalLocationIndicator } from '../universal-location-indicator/universal-location-indicator.component';
import { iconsGap, omniLogo } from './header.css';

export const Header: FunctionComponent = () => {
	const [isTypeAheadOpen, setIsTypeAheadOpen] = useState<boolean>(false);
	const [isLiveChatEnabled] = useFeatures<[boolean, boolean]>([FEATURE_FLAGS.LIVE_CHAT], { isSiteWide: true });
	return (
		<header className="bg-theme-white dn-p" data-testid="headerComponent">
			<AnnouncementBanner
				announcement={{
					message: `Build.com is now Ferguson Home. <a href=${SUPPORT_HOME_LINK}>Learn more</a>`,
					severity: 'LOW',
					id: 'omni-announcement-banner'
				}}
				dismissible={false}
			/>
			<div className="ph3">
				{/* Header Top Row */}
				<div className="dn flex-ns flex-row justify-between mw9 center mt2">
					{/* Location Marker */}
					<UniversalLocationIndicator />
					{/* Top Header Links */}
					<div className="flex flex-row mw9">
						<ProSignupLink />
						<nav className="flex flex-row items-center">
							<div className="mr3">
								<CallCenterLink theme="grey" containerClass={'flex-row-ns hover-theme-primary'} labelClass="f6" />
							</div>
							{isLiveChatEnabled && (
								<div className="mr3">
									<ChatButton
										theme="grey"
										automationHook="header-chat"
										containerClass={'flex-row-ns hover-theme-primary'}
										labelClass="f6"
									/>
								</div>
							)}
							<Link url={SHOWROOM_LINK} className="lh-title hover-theme-primary mr3 f6" color="grey">
								Showrooms
							</Link>
							<Link url={SUPPORT_FAQ_LINK} className="lh-title hover-theme-primary f6" color="grey">
								Help
							</Link>
						</nav>
					</div>
				</div>
				{/* Header Middle Row (Main) */}
				<div className="mw9 dn db-ns pv3 center">
					<div className="flex flex-row justify-between items-center w-100">
						<Link url={HOMEPAGE_LINK} className="dib h2" ariaLabel="Navigate to home page">
							<OmniHomeSvg className={`h-100 ${omniLogo}`} />
						</Link>
						<div className="flex items-center justify-between flex-nowrap pl3 w-90 relative">
							<SearchBarProvider>
								<SearchBar onTypeaheadOpenChanged={setIsTypeAheadOpen} />
							</SearchBarProvider>
							<div className={`fr flex justify-end items-center pl5-l pl3-m ${iconsGap}`}>
								{!isTypeAheadOpen && (
									<>
										<ProjectHeaderDropdown />
										<AccountDropdown />
										<div className="dn db-l">
											<NotificationsBadge />
										</div>
									</>
								)}

								<div className="f5 b flex flex-column flex-row-ns items-center justify-center">
									<CartLink />
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Header Nav Dropdowns */}
			<div className={`${OVERLAY_MARKER} dn db-ns`} />
			<DesktopNav />
			{/* Header Construct Content */}
			<GlobalContentConstruct />
			{/* Mobile Only */}
			<MobileNav />
		</header>
	);
};
